/**
 * "Últimas marcaciones" del Inicio (006 · T025 · US1-6; research R-30; contracts/staff-ui.md §3).
 *
 * Los tres días operativos más recientes con marcas (ya los trae `useRecentDays()` con
 * `limit=3`: cada día que responde el servidor tiene al menos una marca, así que aquí no se
 * filtra nada), en filas de 48 px agrupadas por día ("Hoy"/"Ayer"/fecha) con un enlace a
 * Asistencia para ver el resto.
 */
import { Link } from '@tanstack/react-router';

import type { StaffAttendanceDay } from '@fermin/contracts';

import { dayLabel, t } from '../../i18n';

import { markLabel } from './journey';

export interface RecentMarksProps {
  days: readonly StaffAttendanceDay[];
  /** Día operativo actual en la zona de la sede principal (para "Hoy"/"Ayer"). */
  today: string;
}

export function RecentMarks({ days, today }: RecentMarksProps) {
  return (
    <section aria-labelledby="home-recent-title" className="flex flex-col gap-fm-3">
      <div className="flex items-center justify-between">
        <h2
          id="home-recent-title"
          className="font-display text-fm-lg font-semibold text-fm-text"
        >
          {t('home.recent.title')}
        </h2>
        <Link
          to="/asistencia"
          data-testid="home-recent-all"
          className="inline-flex min-h-11 items-center text-fm-sm font-semibold text-fm-primary-compact"
        >
          {t('home.recent.viewAll')}
        </Link>
      </div>

      {days.length === 0 ? (
        <p data-testid="home-recent-empty" className="text-fm-base text-fm-text-secondary">
          {t('home.recent.empty')}
        </p>
      ) : (
        <ul data-testid="home-recent" className="flex flex-col divide-y divide-fm-border">
          {days.map((day) => (
            <li
              key={day.operative_day}
              data-testid="home-recent-day"
              data-day={day.operative_day}
              className="flex min-h-12 items-center py-fm-2 text-fm-base text-fm-text"
            >
              <span className="font-semibold">{dayLabel(day.operative_day, today)}</span>
              <span className="text-fm-text-secondary">
                {` · ${day.marks.map(markLabel).join(' · ')}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
